import React, { useState } from 'react';
import styled from 'styled-components';

const AnalyticsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 8px 0;
  }
  
  .subtitle {
    color: ${props => props.theme.colors.textLight};
    font-size: 16px;
  }
`;

const TimeframeSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  
  .timeframe-btn {
    padding: 8px 16px;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 20px;
    background: transparent;
    color: ${props => props.theme.colors.textLight};
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.active, &:hover {
      background: ${props => props.theme.colors.primary};
      color: white;
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 24px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 20px 0;
    color: ${props => props.theme.colors.primary};
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .chart-toggle {
      font-size: 12px;
      font-weight: normal;
      color: ${props => props.theme.colors.textLight};
      cursor: pointer;
      padding: 4px 8px;
      border: 1px solid ${props => props.theme.colors.border};
      border-radius: 12px;
      transition: all 0.2s ease;
      
      &:hover {
        background: ${props => props.theme.colors.border};
      }
    }
  }
`;

const MockChart = styled.div`
  height: ${props => props.height || '300px'};
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.background} 0%, 
    rgba(218, 165, 32, 0.1) 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  .chart-visualization {
    width: 100%;
    height: 100%;
    position: relative;
    
    .data-line {
      position: absolute;
      bottom: 20px;
      left: 20px;
      right: 20px;
      height: 2px;
      background: ${props => props.theme.colors.secondary};
      border-radius: 1px;
      
      &::before {
        content: '';
        position: absolute;
        left: 20%;
        top: -40px;
        width: 2px;
        height: 40px;
        background: ${props => props.theme.colors.secondary};
      }
      
      &::after {
        content: '';
        position: absolute;
        left: 60%;
        top: -80px;
        width: 2px;
        height: 80px;
        background: ${props => props.theme.colors.secondary};
      }
    }
    
    .data-points {
      position: absolute;
      bottom: 18px;
      left: 20px;
      right: 20px;
      display: flex;
      justify-content: space-between;
      
      .point {
        width: 6px;
        height: 6px;
        background: ${props => props.theme.colors.secondary};
        border-radius: 50%;
        position: relative;
        
        &.high {
          transform: translateY(-80px);
        }
        
        &.medium {
          transform: translateY(-40px);
        }
      }
    }
  }
  
  .chart-label {
    position: absolute;
    bottom: 16px;
    left: 20px;
    font-size: 12px;
    color: ${props => props.theme.colors.textLight};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const InsightCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 24px;
  
  .insight-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    
    .icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: ${props => props.color || props.theme.colors.secondary};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
    }
    
    .title {
      font-size: 16px;
      font-weight: 600;
      color: ${props => props.theme.colors.primary};
    }
  }
  
  .metric-value {
    font-size: 24px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 8px;
    
    .unit {
      font-size: 14px;
      font-weight: normal;
      color: ${props => props.theme.colors.textLight};
      margin-left: 4px;
    }
  }
  
  .trend-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    
    .trend {
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 500;
      
      &.positive { color: #4CAF50; }
      &.negative { color: #f44336; }
      &.neutral { color: ${props => props.theme.colors.textLight}; }
    }
    
    .period {
      color: ${props => props.theme.colors.textLight};
    }
  }
`;

const ProgressSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const ProgressCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 24px;
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 20px 0;
    color: ${props => props.theme.colors.primary};
  }
  
  .progress-item {
    margin-bottom: 20px;
    
    .progress-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      
      .label {
        font-size: 14px;
        font-weight: 500;
        color: ${props => props.theme.colors.text};
      }
      
      .value {
        font-size: 14px;
        font-weight: 600;
        color: ${props => props.theme.colors.secondary};
      }
    }
    
    .progress-bar {
      height: 6px;
      background: ${props => props.theme.colors.border};
      border-radius: 3px;
      overflow: hidden;
      
      .progress-fill {
        height: 100%;
        background: ${props => props.color || props.theme.colors.secondary};
        border-radius: 3px;
        transition: width 0.3s ease;
      }
    }
  }
`;

const Analytics = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('7D');
  
  const timeframes = ['24H', '7D', '30D', '90D'];
  
  const analyticsData = {
    weightTrend: { current: 153.4, change: -2.1, period: '30 days' },
    avgCalories: { current: 1847, change: -153, period: '7 days' },
    workoutStreak: { current: 12, change: 5, period: 'days' },
    proteinAvg: { current: 142, change: 8, period: '7 days' },
    sleepAvg: { current: 7.3, change: 0.4, period: '7 days' },
    hydrationRate: { current: 89, change: 12, period: '7 days' }
  };
  
  const weeklyProgress = [
    { label: 'Calorie Target', current: 1847, target: 2000, percentage: 92 },
    { label: 'Protein Goal', current: 142, target: 140, percentage: 101 },
    { label: 'Workout Sessions', current: 4, target: 3, percentage: 133 },
    { label: 'Sleep Quality', current: 85, target: 80, percentage: 106 }
  ];

  return (
    <AnalyticsContainer>
      <PageHeader>
        <h1>Performance Analytics</h1>
        <div className="subtitle">Comprehensive insights into your health optimization journey</div>
      </PageHeader>

      <TimeframeSelector>
        {timeframes.map(timeframe => (
          <button
            key={timeframe}
            className={`timeframe-btn ${selectedTimeframe === timeframe ? 'active' : ''}`}
            onClick={() => setSelectedTimeframe(timeframe)}
          >
            {timeframe}
          </button>
        ))}
      </TimeframeSelector>

      <ChartsGrid>
        <ChartCard>
          <h3>
            Weight Progress
            <span className="chart-toggle">7-day average</span>
          </h3>
          <MockChart height="300px">
            <div className="chart-visualization">
              <div className="data-line"></div>
              <div className="data-points">
                <div className="point"></div>
                <div className="point medium"></div>
                <div className="point high"></div>
                <div className="point medium"></div>
                <div className="point"></div>
                <div className="point high"></div>
                <div className="point medium"></div>
              </div>
            </div>
            <div className="chart-label">Weight trend: -2.1 lbs over 30 days</div>
          </MockChart>
        </ChartCard>

        <ChartCard>
          <h3>Daily Metrics</h3>
          <MockChart height="300px">
            <div className="chart-visualization">
              <div className="data-points">
                <div className="point high"></div>
                <div className="point"></div>
                <div className="point medium"></div>
                <div className="point high"></div>
                <div className="point medium"></div>
              </div>
            </div>
            <div className="chart-label">Calories, protein, activity correlation</div>
          </MockChart>
        </ChartCard>
      </ChartsGrid>

      <StatsGrid>
        <InsightCard color="rgba(76, 175, 80, 0.2)">
          <div className="insight-header">
            <div className="icon" style={{background: '#4CAF50', color: 'white'}}>⚖️</div>
            <div className="title">Weight Progress</div>
          </div>
          <div className="metric-value">
            {analyticsData.weightTrend.current}
            <span className="unit">lbs</span>
          </div>
          <div className="trend-info">
            <span className="trend positive">
              ↘ {Math.abs(analyticsData.weightTrend.change)} lbs
            </span>
            <span className="period">{analyticsData.weightTrend.period}</span>
          </div>
        </InsightCard>

        <InsightCard color="rgba(255, 152, 0, 0.2)">
          <div className="insight-header">
            <div className="icon" style={{background: '#FF9800', color: 'white'}}>🔥</div>
            <div className="title">Avg Daily Calories</div>
          </div>
          <div className="metric-value">
            {analyticsData.avgCalories.current}
            <span className="unit">kcal</span>
          </div>
          <div className="trend-info">
            <span className="trend positive">
              ↘ {Math.abs(analyticsData.avgCalories.change)}
            </span>
            <span className="period">{analyticsData.avgCalories.period}</span>
          </div>
        </InsightCard>

        <InsightCard color="rgba(63, 81, 181, 0.2)">
          <div className="insight-header">
            <div className="icon" style={{background: '#3F51B5', color: 'white'}}>💪</div>
            <div className="title">Workout Streak</div>
          </div>
          <div className="metric-value">
            {analyticsData.workoutStreak.current}
            <span className="unit">days</span>
          </div>
          <div className="trend-info">
            <span className="trend positive">
              ↗ +{analyticsData.workoutStreak.change}
            </span>
            <span className="period">current streak</span>
          </div>
        </InsightCard>

        <InsightCard color="rgba(233, 30, 99, 0.2)">
          <div className="insight-header">
            <div className="icon" style={{background: '#E91E63', color: 'white'}}>🥩</div>
            <div className="title">Avg Protein</div>
          </div>
          <div className="metric-value">
            {analyticsData.proteinAvg.current}
            <span className="unit">g</span>
          </div>
          <div className="trend-info">
            <span className="trend positive">
              ↗ +{analyticsData.proteinAvg.change}g
            </span>
            <span className="period">{analyticsData.proteinAvg.period}</span>
          </div>
        </InsightCard>

        <InsightCard color="rgba(156, 39, 176, 0.2)">
          <div className="insight-header">
            <div className="icon" style={{background: '#9C27B0', color: 'white'}}>😴</div>
            <div className="title">Sleep Quality</div>
          </div>
          <div className="metric-value">
            {analyticsData.sleepAvg.current}
            <span className="unit">hrs</span>
          </div>
          <div className="trend-info">
            <span className="trend positive">
              ↗ +{analyticsData.sleepAvg.change}h
            </span>
            <span className="period">{analyticsData.sleepAvg.period}</span>
          </div>
        </InsightCard>

        <InsightCard color="rgba(0, 188, 212, 0.2)">
          <div className="insight-header">
            <div className="icon" style={{background: '#00BCD4', color: 'white'}}>💧</div>
            <div className="title">Hydration Rate</div>
          </div>
          <div className="metric-value">
            {analyticsData.hydrationRate.current}
            <span className="unit">%</span>
          </div>
          <div className="trend-info">
            <span className="trend positive">
              ↗ +{analyticsData.hydrationRate.change}%
            </span>
            <span className="period">{analyticsData.hydrationRate.period}</span>
          </div>
        </InsightCard>
      </StatsGrid>

      <ProgressSection>
        <ProgressCard>
          <h4>Weekly Performance</h4>
          {weeklyProgress.map((item, index) => (
            <div key={index} className="progress-item">
              <div className="progress-header">
                <span className="label">{item.label}</span>
                <span className="value">{item.current}{item.label.includes('%') ? '%' : ''}/{item.target}{item.label.includes('%') ? '%' : ''}</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${Math.min(item.percentage, 100)}%`,
                    background: item.percentage > 100 ? '#4CAF50' : '#DAA520'
                  }}
                />
              </div>
            </div>
          ))}
        </ProgressCard>

        <ProgressCard>
          <h4>Optimization Insights</h4>
          <div style={{color: '#666', lineHeight: 1.6}}>
            <p style={{marginBottom: '16px'}}>
              <strong>Weight Trend:</strong> Consistent downward trajectory indicates optimal caloric deficit. Current rate: 0.5 lbs/week.
            </p>
            <p style={{marginBottom: '16px'}}>
              <strong>Protein Efficiency:</strong> Exceeding targets by 8g daily. Supports lean mass retention during cut phase.
            </p>
            <p style={{marginBottom: '16px'}}>
              <strong>Training Consistency:</strong> 12-day streak demonstrates adherence. Recovery metrics within optimal range.
            </p>
            <p>
              <strong>Next Optimization:</strong> Consider increasing active recovery sessions to support current training volume.
            </p>
          </div>
        </ProgressCard>
      </ProgressSection>
    </AnalyticsContainer>
  );
};

export default Analytics;